import type { CalculatorContent, Faq } from '@/types/calculator'
import type { MarkupProfitInputs } from './types'

export const content: CalculatorContent<MarkupProfitInputs> = {
  intro:
    'Price products correctly with this markup calculator: enter cost, selling price, quantity and overheads to see gross profit, net profit, margin and break-even units — and understand the difference between profit margin vs markup.',
  howItWorks: [
    'Revenue is selling price times quantity; total cost adds per-unit costs across the quantity plus your fixed overhead figure.',
    'Markup expresses profit as a percentage of cost, while margin expresses it as a percentage of selling price. They measure the same profit differently, which is why confusing the two leads to underpricing.',
    'Break-even units divide your overhead by the contribution per unit (selling price minus cost). The calculator also shows the price needed for a target 30% margin as a quick reference.',
    'All figures exclude VAT unless you enter VAT-inclusive amounts yourself — be consistent so markup and margin stay meaningful.',
  ],
  formulas: [
    {
      label: 'Revenue',
      expression: 'selling price × quantity',
    },
    {
      label: 'Gross profit',
      expression: 'revenue − (cost per unit × quantity)',
    },
    {
      label: 'Net profit',
      expression: 'revenue − total cost (variable + overhead)',
    },
    {
      label: 'Markup',
      expression: '(selling price − cost) ÷ cost',
    },
    {
      label: 'Margin',
      expression: 'gross profit ÷ revenue',
      notes: 'A 50% markup equals roughly a 33% margin — markup is always larger than margin.',
    },
    {
      label: 'Break-even units',
      expression: 'overhead ÷ (selling price − cost per unit)',
    },
  ],
  example: {
    title: 'Selling 100 units with R500 overhead',
    description:
      'A small business buys a product at a unit cost, sells it at a markup and wants to see profit, margin and the break-even point.',
    inputs: {
      costPerUnit: 50,
      sellingPricePerUnit: 75,
      quantity: 100,
      overhead: 500,
    },
  },
  definitions: [
    {
      term: 'Markup',
      description: 'Profit expressed as a percentage of the cost price.',
    },
    {
      term: 'Margin',
      description:
        'Profit expressed as a percentage of the selling price. A 100% markup is a 50% margin.',
    },
    {
      term: 'Gross profit',
      description: 'Revenue minus the direct (variable) cost of the units sold.',
    },
    {
      term: 'Net profit',
      description: 'What remains after variable costs and fixed overheads are deducted.',
    },
    {
      term: 'Break-even point',
      description: 'The number of units you must sell before profit turns positive.',
    },
  ],
}

export const faqs: Faq[] = [
  {
    question: 'What is the difference between markup and margin?',
    answer:
      'Markup is the profit as a percentage of cost. Margin is the profit as a percentage of selling price. A 50% markup equals a 33.3% margin.',
  },
  {
    question: 'How do I convert markup to margin?',
    answer:
      'Margin = markup ÷ (1 + markup). For example, a 25% markup is 0.25 ÷ 1.25 = a 20% margin.',
  },
  {
    question: 'Does this calculator include VAT?',
    answer:
      'No. Enter either VAT-exclusive or VAT-inclusive figures consistently — the calculations apply to whatever amounts you supply.',
  },
  {
    question: 'What margin should a small business aim for?',
    answer:
      'It varies widely by industry. The reference price for a 30% margin shown in the results is a starting point, not a recommendation.',
  },
]
